import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { TicketRequestType } from '../types'

/** "Diğer" sistem türü — silinemez/pasifleştirilemez/düzenlenemez/taşınamaz (create akışı buna bağlı). */
const OTHER_ID = -1

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen (sıralı) satırlar — okların komşu-koruma hesabı için. */
  rows: TicketRequestType[]
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: TicketRequestType, dir: 'up' | 'down') => void
  onEdit: (row: TicketRequestType) => void
  onToggle: (row: TicketRequestType, active: boolean) => void
  onDelete: (row: TicketRequestType) => void
}

/**
 * Talep türü tablosunun türe-özel sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu
 * yan yana ↑/↓ okları (ortak SortSpinner). Durum satır-içi InputSwitch (Aktif/Pasif; anayasa lookup ekranı)
 * + satır aksiyonları (düzenle/sil). "Diğer" (-1) satırında tüm değiştirici kontroller pasiftir (korunur) —
 * satır kendi okları kilitli olduğu gibi, komşu satırların onu iten okları da kilitlidir (yerinde sabit).
 */
export function ticketTypeColumns({
  t,
  localized,
  busyId,
  rows,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  const rowCount = rows.length
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('admin.types.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: TicketRequestType, { rowIndex }) => {
        const isProtected = row.id === OTHER_ID
        return (
          <SortSpinner
            onUp={() => onMove(row, 'up')}
            onDown={() => onMove(row, 'down')}
            disableUp={rowIndex === 0 || isProtected || rows[rowIndex - 1]?.id === OTHER_ID}
            disableDown={
              rowIndex === rowCount - 1 || isProtected || rows[rowIndex + 1]?.id === OTHER_ID
            }
            busy={reordering}
            labelUp={t('common.reorder.up')}
            labelDown={t('common.reorder.down')}
          />
        )
      }}
    />,
    <Column
      key="name"
      header={t('admin.types.columns.name')}
      body={(row: TicketRequestType) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('admin.types.columns.status')}
      style={{ width: '8rem' }}
      body={(row: TicketRequestType) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('admin.types.status.active') : t('admin.types.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.types.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: TicketRequestType) => {
        const active = row.recordStatus === 1
        const protectedRow = row.id === OTHER_ID
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={protectedRow || busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('admin.types.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('admin.types.actions.edit')}
              tooltip={t('admin.types.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={protectedRow}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('admin.types.actions.delete')}
              tooltip={t('admin.types.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={protectedRow || busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
